import { put, call, all, takeEvery, takeLatest, delay } from 'redux-saga/effects'
import { withCallback } from 'redux-saga-callback'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import { message } from 'antd'
import {
  list as usersList,
  count as usersCount,
  add as userAdd,
  get as userGet,
  update as userUpdate,
  del as userDelete,
  search as usersSearch,
  inactiveSearch as usersInactiveSearch,
  friendList as userFriendList,
  groupList as userGroupList,
  authTokenList as userAuthTokenList,
  authTokenDel as userAuthTokenDelete,
  authTokenAdd as userAuthTokenAdd,
  authTokenSearch as userAuthTokensSearch,
  deactivate as userDeactivate,
  activate as userActivate,
  aiAgentUserList as usersAIAgentList,
  updateFriends as userUpdateFriends,
  userSearch as userFriendSearch,
  removeFriends as userRemoveFriends,
  groupSearch as userGroupSearch,
  kickFromGroup as kickUserFromGroup,
  banFromGroup as banUserFromGroup,
  addToGroup as addUserToGroup,
  ChatApp,
  UsersListFilters,
  ChatUser,
  UsersListResponse,
  UsersCountResponse,
  UserResponse,
  UserGroupsListResponse,
  AuthTokensListResponse,
  AuthTokenResponse,
  DeactivateResponse,
  ActivateResponse,
  UpdateFriendsResponse,
  GroupMembersChangeResponse,
  StatusCount,
  ApiErrorBody,
  AddUserPayload,
  UpdateUserPayload as UpdateUserServicePayload,
} from 'services/users'
import { list as groupsList, search as groupsSearch } from 'services/groups'
import { manualOutreach } from 'services/user'
import _ from 'lodash'
import constants from 'constants'
import { getAppHost, isAuthErrorCode } from 'helpers'
import actions from './actions'
import { UsersState, LoosePagination } from './reducers'

/**
 * Users slice sagas. Ported from the legacy flat `redux/apps/sagas.js` users
 * section — reconciled to parity (docs/users-migration/PARITY-AUDIT.md) before
 * this .ts conversion (docs/users-migration/USERS-MIGRATION-PLAN.md D3).
 * Types derived from captured staging responses: docs/users-migration/Api-response/.
 */

// The root store is a plain-JS module (untyped). Read its slices through this
// boundary so the rest of the file stays typed. `apps` is untyped legacy state.
interface RootState {
  apps: { active: ChatApp; appSettings: any; groupsList: unknown[] }
  users: UsersState
}
const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

// Every saga is dispatched with `{ type, payload }`; only the payload varies.
type Saga<P> = { payload?: P }

// redux-saga@1's take* overloads infer the action type from the worker's param;
// a param narrowed to `{ payload }` doesn't satisfy their `A extends Action`
// constraint, so they fall back to the channel overload and reject the string
// pattern ("string not assignable to TakeableChannel"). We wire workers through
// this type at the registration boundary — runtime is unchanged.
type SagaWorker = (action: any) => any

// Sums the per-status counts returned by the `count=1` users endpoint.
// `data` is an array of { status, count } for the unfiltered/multi-status case,
// but collapses to a single { status, count } object when a `status` filter is
// applied — handle both. Anything else (error object, missing data) -> 0.
function sumStatusCounts(response: UsersCountResponse): number {
  if (!response || !response.data) return 0
  const groups = Array.isArray(response.data) ? response.data : [response.data]
  return groups.reduce(
    (total: number, item: StatusCount) => total + (Number(item && item.count) || 0),
    0,
  )
}

// Message for a failed mutation. `details` maps field -> messages and is present
// only on ERR_BAD_REQUEST, where a field entry may be a string OR an array of
// them; `message` is the one field always populated. Reading `details.name`
// alone rendered "[...]" for the array case and an EMPTY error toast whenever
// the API rejected on any other field.
function apiErrorMessage(error: ApiErrorBody | undefined, fallback: string): string {
  const detail = error?.details?.name
  const fieldMessage = Array.isArray(detail) ? detail[0] : detail
  return fieldMessage || error?.message || fallback
}

interface UsersListPayload {
  data?: ChatApp
  appSettings?: any
  currentPage?: number
  filters?: UsersListFilters & { createdAt?: [string, string] }
}

export function* USERS_LIST({ payload }: Saga<UsersListPayload> & { payload: UsersListPayload }) {
  try {
    const filters = payload.filters || {}
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
        usersFilters: filters,
      },
    })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const hasFilters = Object.values(filters).some(v => (Array.isArray(v) ? v.length > 0 : !!v))
    // Trimmed like SEARCH_USER — this serves page 2+ of an active search.
    const searchText = hasFilters ? '' : getRoot().users.userSearchText.trim()
    const usersPagination = getRoot().users.usersListPagination

    let pageNo = payload.currentPage
    if (hasFilters && pageNo === undefined) {
      pageNo = 1
    } else if (pageNo === undefined && usersPagination.current_page !== undefined) {
      pageNo = usersPagination.current_page + 1
    }

    const serviceFilters: UsersListFilters & { createdAt?: [string, string] } = { ...filters }
    if (serviceFilters.createdAt && serviceFilters.createdAt.length === 2) {
      const [fromDate, toDate] = serviceFilters.createdAt
      serviceFilters.createdAtFrom = Math.floor(new Date(`${fromDate}T00:00:00`).getTime() / 1000)
      serviceFilters.createdAtTo = Math.floor(new Date(`${toDate}T23:59:59`).getTime() / 1000)
    }
    delete serviceFilters.createdAt

    const success: UsersListResponse = yield call(
      usersList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText,
      0,
      serviceFilters,
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let usersListPagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        usersListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          usersList: success.data,
          usersListTotal: totalUserCount,
          usersListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersList: [],
          usersListTotal: 0,
          usersListPagination: [],
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch users', 'error')
  }
}

interface UsersCountPayload {
  data?: ChatApp
  appSettings?: any
}

export function* USERS_COUNT({ payload = {} }: Saga<UsersCountPayload>) {
  const data = payload.data || getRoot().apps.active
  const appSettings = payload.appSettings || getRoot().apps.appSettings

  // Counts need a resolved, non-legacy app: on hard refresh `apps.active` is
  // still {} (the URL would become /apps/undefined/...), and the legacy region
  // has no chat-api counts endpoint. Callers refetch once the app resolves.
  if (!data?.id || data.region === 'legacy') return

  const host = getAppHost(appSettings)

  yield put({
    type: actions.SET_STATE,
    payload: { areUserCountsLoading: true },
  })

  try {
    const [activeRes, inactiveRes]: UsersCountResponse[] = yield all([
      call(usersCount, data, host, 0),
      call(usersCount, data, host, 1),
    ])

    const usersActiveCount = sumStatusCounts(activeRes)
    const usersInactiveCount = sumStatusCounts(inactiveRes)

    yield put({
      type: actions.SET_STATE,
      payload: {
        usersActiveCount,
        usersInactiveCount,
        usersTotalCount: usersActiveCount + usersInactiveCount,
        areUserCountsLoading: false,
      },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areUserCountsLoading: false },
    })
    window.triggerNotification('Failed to fetch user counts', 'error')
  }
}

export function* INACTIVE_USERS_LIST({
  payload,
}: Saga<UsersListPayload> & { payload: UsersListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const filters = payload.filters || {}
    const hasFilters = Object.values(filters).some(v => (Array.isArray(v) ? v.length > 0 : !!v))
    // Trimmed like SEARCH_INACTIVE_USER — this serves page 2+ of that search.
    const searchText = hasFilters ? '' : getRoot().users.userInactiveSearchText.trim()
    const usersInactivePagination = getRoot().users.usersListInactivePagination

    let pageNo = payload.currentPage
    if (hasFilters && pageNo === undefined) {
      pageNo = 1
    } else if (pageNo === undefined && usersInactivePagination.current_page !== undefined) {
      pageNo = usersInactivePagination.current_page + 1
    }

    const serviceFilters: UsersListFilters & { createdAt?: [string, string] } = { ...filters }
    if (serviceFilters.createdAt && serviceFilters.createdAt.length === 2) {
      const [fromDate, toDate] = serviceFilters.createdAt
      serviceFilters.createdAtFrom = Math.floor(new Date(`${fromDate}T00:00:00`).getTime() / 1000)
      serviceFilters.createdAtTo = Math.floor(new Date(`${toDate}T23:59:59`).getTime() / 1000)
    }
    delete serviceFilters.createdAt

    const success: UsersListResponse = yield call(
      usersList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText,
      1,
      serviceFilters,
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let usersListInactivePagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        usersListInactivePagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          usersInactiveList: success.data,
          usersListInactiveTotal: totalUserCount,
          usersListInactivePagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersInactiveList: success ? success.data : [],
          usersListInactiveTotal: 0,
          usersListInactivePagination: [],
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch deactivated users', 'error')
  }
}

interface GetUserPayload {
  id: string
}

export function* GET_USER({ payload }: Saga<GetUserPayload> & { payload: GetUserPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const { id } = payload
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uAuthTokensListPagination } = getRoot().users
    const success: UserResponse = yield call(userGet, id, activeApp, getAppHost(appSettings))
    const authTokensRes: AuthTokensListResponse = yield call(
      userAuthTokenList,
      id,
      activeApp,
      getAppHost(appSettings),
    )
    const groupsRes: UserGroupsListResponse = yield call(
      userGroupList,
      id,
      activeApp,
      getAppHost(appSettings),
      1,
    )
    const friendsRes: UsersListResponse = yield call(
      userFriendList,
      id,
      activeApp,
      getAppHost(appSettings),
    )
    if (success && success.data) {
      // The user itself loaded — auth tokens/groups/friends are secondary tabs
      // on this same page, so a failure in any one of them (network blip, a
      // transient 5xx) must not crash the whole profile load. Each falls back
      // to an empty list/pagination instead of throwing on `.data`/`.meta`.
      const groupsData = groupsRes && groupsRes.data ? groupsRes.data : []
      let totalUserGroupCount = groupsData.length
      let uGroupsListPagination: LoosePagination = {}
      if (groupsRes && groupsRes.meta && groupsRes.meta.pagination) {
        uGroupsListPagination = groupsRes.meta.pagination
        if (groupsRes.meta.pagination.total_pages) {
          totalUserGroupCount =
            groupsRes.meta.pagination.total_pages * groupsRes.meta.pagination.per_page
        }
      }

      const friendsData = friendsRes && friendsRes.data ? friendsRes.data : []
      let totalUserFriendCount = friendsData.length
      let uFriendsListPagination: LoosePagination = {}
      if (friendsRes && friendsRes.meta && friendsRes.meta.pagination) {
        uFriendsListPagination = friendsRes.meta.pagination
        if (friendsRes.meta.pagination.total_pages) {
          totalUserFriendCount =
            friendsRes.meta.pagination.total_pages * friendsRes.meta.pagination.per_page
        }
      }

      let userNonFriendsList: ChatUser[] = getRoot().users.usersList

      if (userNonFriendsList.length > 0) {
        userNonFriendsList = _.filter(userNonFriendsList, function fn(unf: ChatUser) {
          return unf.uid !== id
        })
      }

      const authTokensData = authTokensRes && authTokensRes.data ? authTokensRes.data : []
      let totalUAuthTokenCount = authTokensData.length
      let uAuthTokensPagination = uAuthTokensListPagination
      if (
        authTokensRes &&
        authTokensRes.meta &&
        authTokensRes.meta.pagination &&
        authTokensRes.meta.pagination.total_pages
      ) {
        totalUAuthTokenCount =
          authTokensRes.meta.pagination.total_pages * authTokensRes.meta.pagination.per_page
        uAuthTokensPagination = authTokensRes.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersActive: success.data,
          uAuthTokensList: authTokensData,
          uAuthTokensListTotal: totalUAuthTokenCount,
          uAuthTokensListPagination: uAuthTokensPagination,
          uGroupsList: groupsData,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination,
          uFriendsList: friendsData,
          uFriendsListTotal: totalUserFriendCount,
          uFriendsListPagination,
          userNonGroupsList: getRoot().apps.groupsList,
          userNonFriendsList,
          groupSearchText: '',
          areUsersLoading: false,
        },
      })
      if (!(groupsRes && groupsRes.data) || !(friendsRes && friendsRes.data) || !(authTokensRes && authTokensRes.data)) {
        window.triggerNotification('Some user details failed to load', 'error')
      }
    } else {
      yield put(push(`/app/${activeApp.id}/users`))
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch user', 'error')
  }
}

export function* ADD_USER({
  payload,
}: Saga<Record<string, unknown>> & { payload: Record<string, unknown> }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const filtered: Record<string, unknown> = {}
    _.filter(payload, function fn(n, k) {
      if (payload[k] && _.trim(payload[k] as string) !== '') {
        filtered[k] = n
      }
    })

    const success: UserResponse = yield call(
      userAdd,
      filtered as unknown as AddUserPayload,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      yield put({
        type: actions.USERS_LIST,
        payload: {
          currentPage: 1,
          filters: {},
          data: activeApp,
          appSettings,
        },
      })

      yield put({ type: actions.USERS_COUNT, payload: {} })

      yield put(push(`/app/${activeApp.id}/users`))
      window.triggerNotification(
        filtered.name ? `User ${filtered.name} added` : 'User added',
        'success',
      )
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })

      if (success.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value: string | string[]) {
          message.error(value)
        })
      } else if (success.error?.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to add user', 'error')
  }
}

interface EnterTextPayload {
  searchText: string
  clearText?: boolean
}

export function* ENTER_USER_TEXT({ payload }: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userSearchText: '',
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userSearchText: searchText,
        },
      })

      // Bug fix (manual QA, 2026-08-06): the legacy gate was
      // `length === 0 || >= 2`, so a single character never fired SEARCH_USER
      // and the list kept showing unfiltered results. Search on any non-empty
      // length — same fix already applied to the Groups slice.
      //
      // Gate on the trimmed length so a whitespace-only term restores the full
      // list instead of searching for " " (zero rows). State keeps the raw text
      // — it is the input's value, so trimming it would block typing a space.
      if (searchText.trim().length === 0) {
        yield put({
          type: actions.USERS_LIST,
          payload: {
            currentPage: 1,
          },
        })
      } else {
        // Debounce, and the watcher below uses takeLatest so a newer keystroke
        // cancels this delay. Without both, dropping the >= 2 gate means every
        // keystroke fires an uncancelled request and a slower earlier response
        // can overwrite a newer one. Mirrors groups/sagas.js ENTER_GROUP_MEMBER_TEXT.
        yield delay(300)
        yield put({
          type: actions.SEARCH_USER,
          payload: {},
        })
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search users', 'error')
  }
}

export function* SEARCH_USER() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    // Trimmed at the API boundary: state keeps what the user typed, the
    // endpoint gets a term it can prefix-match.
    const searchText = getRoot().users.userSearchText.trim()

    const success: UsersListResponse = yield call(
      usersSearch,
      activeApp,
      getAppHost(appSettings),
      searchText,
    )

    if (success && success.data) {
      // `pagination.total` is 0 even with results, so reading it alone left the
      // total at data.length (10) — one page, and the table hid its pagination.
      // Derive from `total_pages * per_page` like USERS_LIST does.
      const paginationMeta = success.meta?.pagination
      let totalUserCount = success.data.length
      if (paginationMeta?.total_pages) {
        totalUserCount = paginationMeta.total_pages * paginationMeta.per_page
      } else if (paginationMeta?.total) {
        totalUserCount = paginationMeta.total
      }
      const usersListPagination: LoosePagination = paginationMeta ?? {}
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersList: success.data,
          usersListTotal: totalUserCount,
          usersListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersListTotal: 0,
          usersListPagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search users', 'error')
  }
}

export function* ENTER_INACTIVE_USER_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userInactiveSearchText: '',
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userInactiveSearchText: searchText,
        },
      })

      // Debounce + takeLatest (see ENTER_USER_TEXT): the >= 2 gate is gone, so
      // without it every keystroke fires an uncancelled request.
      yield delay(300)
      yield put({
        type: actions.SEARCH_INACTIVE_USER,
        payload: {},
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search deactivated users', 'error')
  }
}

export function* SEARCH_INACTIVE_USER() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    // Trimmed like SEARCH_USER. A whitespace-only term becomes '', which the
    // endpoint answers with the full deactivated list.
    const searchText = getRoot().users.userInactiveSearchText.trim()

    const success: UsersListResponse = yield call(
      usersInactiveSearch,
      activeApp,
      getAppHost(appSettings),
      searchText,
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersInactiveList: success.data,
          usersListInactiveTotal: totalUserCount,
          usersListInactivePagination: success.meta.pagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          usersListInactiveTotal: 0,
          usersListInactivePagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search deactivated users', 'error')
  }
}

interface UpdateUserPayload {
  id: string
  payload: Record<string, unknown>
}

export function* UPDATE_USER({ id, payload }: UpdateUserPayload) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const filtered: Record<string, unknown> = {}
    const unset: string[] = []

    _.filter(payload, function fn(n, k) {
      if (payload[k] && _.trim(payload[k] as string) !== '') {
        filtered[k] = n
      } else if (k === 'tags') {
        filtered.tags = []
      } else unset.push(k)
    })

    filtered.unset = unset

    const success: UserResponse = yield call(
      userUpdate,
      id,
      filtered as unknown as UpdateUserServicePayload,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      yield put({
        type: actions.USERS_LIST,
        payload: { currentPage: 1, data: activeApp, appSettings },
      })

      yield put(push(`/app/${activeApp.id}/users`))
      window.triggerNotification(
        filtered.name ? `User ${filtered.name} edited` : 'User edited',
        'success',
      )
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value: string | string[]) {
          message.error(value)
        })
      } else if (success.error?.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to update user', 'error')
  }
}

interface UserMutationPayload {
  id: string
  refresh?: boolean
  filters?: UsersListFilters
}

export function* DELETE_USER({
  payload,
}: Saga<UserMutationPayload> & { payload: UserMutationPayload }) {
  try {
    const { id, refresh, filters } = payload

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { usersListPagination } = getRoot().users
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const success: UserResponse = yield call(userDelete, id, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      const userListPageNo =
        usersListPagination !== undefined && (usersListPagination.count ?? 0) > 1
          ? usersListPagination.current_page
          : 1
      yield put({
        type: actions.USERS_LIST,
        payload: { currentPage: userListPageNo, filters: filters || {}, data: activeApp, appSettings },
      })
      yield put({ type: actions.USERS_COUNT, payload: {} })
      window.triggerNotification('User Deleted', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/users`))
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(apiErrorMessage(success.error, 'Failed to delete user'), 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to delete user', 'error')
  }
}

export function* DEACTIVATE_USER({
  payload,
}: Saga<UserMutationPayload> & { payload: UserMutationPayload }) {
  try {
    const { id, refresh, filters } = payload

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { usersListPagination, usersListInactivePagination } = getRoot().users

    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const success: DeactivateResponse = yield call(
      userDeactivate,
      id,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userSearchText: '',
          userInactiveSearchText: '',
        },
      })
      const userListPageNo =
        usersListPagination !== undefined && (usersListPagination.count ?? 0) > 1
          ? usersListPagination.current_page
          : 1
      const userInactiveListPageNo =
        usersListInactivePagination !== undefined && (usersListInactivePagination.count ?? 0) < 10
          ? usersListInactivePagination.current_page
          : 1
      yield put({
        type: actions.USERS_LIST,
        payload: { currentPage: userListPageNo, filters: filters || {} },
      })
      yield put({
        type: actions.USERS_INACTIVE_LIST,
        payload: { currentPage: userInactiveListPageNo },
      })
      yield put({ type: actions.USERS_COUNT, payload: {} })
      window.triggerNotification('User deactivated', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/users`))
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(
          apiErrorMessage(success.error, 'Failed to deactivate user'),
          'error',
        )
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to deactivate user', 'error')
  }
}

export function* ACTIVATE_USER({
  payload,
}: Saga<UserMutationPayload> & { payload: UserMutationPayload }) {
  try {
    const { id, refresh, filters } = payload

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { usersListPagination, usersListInactivePagination } = getRoot().users

    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const success: ActivateResponse = yield call(
      userActivate,
      id,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userSearchText: '',
          userInactiveSearchText: '',
        },
      })
      const userListPageNo =
        usersListPagination !== undefined && (usersListPagination.count ?? 0) < 10
          ? usersListPagination.current_page
          : 1

      const userInactiveListPageNo =
        usersListInactivePagination !== undefined && (usersListInactivePagination.count ?? 0) > 1
          ? usersListInactivePagination.current_page
          : 1
      yield put({
        type: actions.USERS_LIST,
        payload: {
          currentPage: userListPageNo,
        },
      })
      yield put({
        type: actions.USERS_INACTIVE_LIST,
        payload: { currentPage: userInactiveListPageNo, filters: filters || {} },
      })
      yield put({ type: actions.USERS_COUNT, payload: {} })
      window.triggerNotification('User activated', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/users`))
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(
          apiErrorMessage(success.error, 'Failed to activate user'),
          'error',
        )
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to activate user', 'error')
  }
}

interface AuthTokenListPayload {
  data?: ChatApp
  appSettings?: any
  currentPage?: number
}

export function* USER_AUTH_TOKENS_LIST({
  payload,
}: Saga<AuthTokenListPayload> & { payload: AuthTokenListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const uAuthTokensPagination = getRoot().users.uAuthTokensListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && uAuthTokensPagination && uAuthTokensPagination.current_page !== undefined)
      pageNo = uAuthTokensPagination.current_page + 1

    const success: AuthTokensListResponse = yield call(
      userAuthTokenList,
      (getRoot().users.usersActive as ChatUser).uid,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
    )

    if (success && success.data) {
      let totalUAuthTokenCount = success.data.length
      let uAuthTokensListPagination = [] as LoosePagination

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUAuthTokenCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        uAuthTokensListPagination = success.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          uAuthTokensList: success.data,
          uAuthTokensListTotal: totalUAuthTokenCount,
          uAuthTokensListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          uAuthTokensList: [],
          uAuthTokensListTotal: 0,
          uAuthTokensListPagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch auth tokens', 'error')
  }
}

interface AddAuthTokenPayload {
  uid: string
}

export function* ADD_USER_AUTH_TOKEN({
  payload,
}: Saga<AddAuthTokenPayload> & { payload: AddAuthTokenPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uAuthTokensListPagination } = getRoot().users

    const { uid } = payload

    const success: AuthTokenResponse = yield call(
      userAuthTokenAdd,
      uid,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      const uAuthTokenListPageNo =
        uAuthTokensListPagination !== undefined &&
        uAuthTokensListPagination.count === uAuthTokensListPagination.per_page
          ? (uAuthTokensListPagination.current_page ?? 0) + 1
          : uAuthTokensListPagination.current_page

      yield put({
        type: actions.USER_AUTH_TOKENS_LIST,
        payload: { currentPage: uAuthTokenListPageNo, data: activeApp },
      })

      window.triggerNotification('Auth Token created', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })

      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(
          apiErrorMessage(success.error, 'Failed to create auth token'),
          'error',
        )
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to create auth token', 'error')
  }
}

interface DeleteAuthTokenPayload {
  authToken: string
  uid: string
  refresh?: boolean
}

export function* DELETE_USER_AUTH_TOKEN({
  payload,
}: Saga<DeleteAuthTokenPayload> & { payload: DeleteAuthTokenPayload }) {
  try {
    const { authToken, uid, refresh } = payload

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uAuthTokensListPagination } = getRoot().users

    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const success: AuthTokenResponse = yield call(
      userAuthTokenDelete,
      authToken,
      uid,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      const uAuthTokenListPageNo =
        uAuthTokensListPagination !== undefined && (uAuthTokensListPagination.count ?? 0) > 1
          ? uAuthTokensListPagination.current_page
          : 1
      yield put({
        type: actions.USER_AUTH_TOKENS_LIST,
        payload: { currentPage: uAuthTokenListPageNo, data: activeApp },
      })
      window.triggerNotification('Auth Token deleted', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/users/user/${uid}`))
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(
          apiErrorMessage(success.error, 'Failed to delete auth token'),
          'error',
        )
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to delete auth token', 'error')
  }
}

interface EnterAuthTokenTextPayload {
  searchText: string
}

export function* ENTER_UAUTHTOKEN_TEXT({
  payload,
}: Saga<EnterAuthTokenTextPayload> & { payload: EnterAuthTokenTextPayload }) {
  try {
    const { searchText } = payload
    yield put({
      type: actions.SET_STATE,
      payload: {
        uAuthTokenSearchText: searchText,
        areUsersLoading: true,
      },
    })
    // Debounce + takeLatest (see ENTER_USER_TEXT): the >= 2 gate is gone, so
    // without it every keystroke fires an uncancelled request.
    yield delay(300)
    yield put({
      type: actions.SEARCH_UAUTHTOKEN,
      payload: {},
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search auth tokens', 'error')
  }
}

export function* SEARCH_UAUTHTOKEN() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const activeUser = getRoot().users.usersActive as ChatUser
    const searchText = getRoot().users.uAuthTokenSearchText

    const success: AuthTokensListResponse = yield call(
      userAuthTokensSearch,
      activeUser.uid,
      activeApp,
      getAppHost(appSettings),
      searchText,
    )

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          uAuthTokensList: success.data,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search auth tokens', 'error')
  }
}

interface ImportUsersPayload {
  importFrom: string
}

export function* IMPORT_USERS_REQUEST_MANUAL_INTENT({
  payload,
}: Saga<ImportUsersPayload> & { payload: ImportUsersPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    const activeApp = getRoot().apps.active
    if (!_.isEmpty(activeApp) && activeApp.id !== undefined) {
      const intent = _.join(
        [(constants as any).ManualOutreachIntents['import-users-request'], activeApp.id, payload.importFrom],
        ' / ',
      )
      const post = { intent }

      const success: any = yield call(manualOutreach, post)
      if (success && success.data) {
        window.triggerNotification(`Response recorded, we'll get back to you soon!`, 'success')
        yield put({
          type: 'settings/CHANGE_SETTING',
          payload: {
            setting: 'visibleModalID',
            value: '',
          },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            areUsersLoading: false,
          },
        })
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            areUsersLoading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else yield put(push(`/login`))
        } else if (success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        }
      }
      return success
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to submit import request', 'error')
  }
}

interface ShowImportUserRequestPayload {
  intent: string
}

export function* SHOW_IMPORT_USER_REQUEST({
  payload,
}: Saga<ShowImportUserRequestPayload> & { payload: ShowImportUserRequestPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        currentIntentRequest: '',
      },
    })
    const { intent: currentIntentRequest } = payload
    yield put({
      type: actions.SET_STATE,
      payload: {
        currentIntentRequest,
      },
    })
    yield put({
      type: 'settings/SET_STATE',
      payload: {
        visibleModalID: 'importUserRequest',
      },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to show import request', 'error')
  }
}

export function* AI_AGENT_USER_LIST({
  payload,
}: Saga<UsersListPayload> & { payload: UsersListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areAIAgentUsersLoading: true,
      },
    })

    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const searchText = getRoot().users.aiAgentUserSearchText
    const usersPagination = getRoot().users.aiAgentUserListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && usersPagination && usersPagination.current_page !== undefined)
      pageNo = usersPagination.current_page + 1
    pageNo = 1
    const success: UsersListResponse = yield call(
      usersAIAgentList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText,
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let aiAgentUserListPagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        aiAgentUserListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          aiAgentUserList: success.data,
          aiAgentUserListTotal: totalUserCount,
          aiAgentUserListPagination,
          areAIAgentUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          aiAgentUserList: [],
          aiAgentUserListTotal: 0,
          aiAgentUserListPagination: [],
          areAIAgentUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areAIAgentUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch AI agent users', 'error')
  }
}

export function* USER_FRIENDS_LIST({
  payload,
}: Saga<UsersListPayload> & { payload: UsersListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const searchText = getRoot().users.userFriendSearchText
    const uFriendsPagination = getRoot().users.uFriendsListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && uFriendsPagination && uFriendsPagination.current_page !== undefined)
      pageNo = uFriendsPagination.current_page + 1

    const success: UsersListResponse = yield call(
      userFriendList,
      (getRoot().users.usersActive as ChatUser).uid,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText,
    )
    if (success && success.data) {
      let totalUserFriendCount = success.data.length
      let uFriendsListPagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserFriendCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        uFriendsListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: success.data,
          uFriendsListTotal: totalUserFriendCount,
          uFriendsListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: [],
          uFriendsListTotal: 0,
          uFriendsListPagination: [],
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch friends', 'error')
  }
}

export function* ENTER_USER_FRIEND_LIST_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userFriendSearchText: '',
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userFriendSearchText: searchText,
          areUsersLoading: true,
        },
      })
      // Debounce + takeLatest (see ENTER_USER_TEXT): the >= 2 gate is gone, so
      // without it every keystroke fires an uncancelled request.
      yield delay(300)
      yield put({
        type: actions.SEARCH_USER_FRIEND_LIST,
        payload: {},
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search friends', 'error')
  }
}

export function* SEARCH_USER_FRIEND_LIST() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const { appSettings } = getRoot().apps
    const searchText = getRoot().users.userFriendSearchText
    const success: UsersListResponse = yield call(
      userFriendSearch,
      (getRoot().users.usersActive as ChatUser).uid,
      getRoot().apps.active,
      getAppHost(appSettings),
      searchText,
    )
    if (success && success.data) {
      let userFriendsListTotal = success.data.length

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        userFriendsListTotal = success.meta.pagination.total_pages * success.meta.pagination.per_page
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: success.data,
          uFriendsListTotal: userFriendsListTotal,
          uFriendsListPagination: success.meta.pagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsListTotal: 0,
          uFriendsListPagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search friends', 'error')
  }
}

interface UpdateUserFriendsPayload {
  uid: string
  values: { accepted: string[] }
}

export function* UPDATE_USER_FRIENDS({
  payload,
}: Saga<UpdateUserFriendsPayload> & { payload: UpdateUserFriendsPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uid, values } = payload
    const { uFriendsList } = getRoot().users

    const success: UpdateFriendsResponse = yield call(
      userUpdateFriends,
      uid,
      values,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      let unfriendUIDs: string[] = []
      _.forEach(uFriendsList, function fn1(f: ChatUser) {
        const checkIndex = _.findIndex(values.accepted, function fn2(a) {
          return a === f.uid
        })
        if (checkIndex < 0) unfriendUIDs = _.concat(unfriendUIDs, f.uid)
      })

      if (unfriendUIDs.length) {
        yield call(userRemoveFriends, uid, unfriendUIDs, activeApp, getAppHost(appSettings))
      }

      const friendsRes: UsersListResponse = yield call(
        userFriendList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )

      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: friendsRes.data,
          areUsersLoading: false,
        },
      })
      window.triggerNotification('Friends updated!', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })

      if (success.error?.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(
          apiErrorMessage(success.error, 'Failed to update friends'),
          'error',
        )
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to update friends', 'error')
  }
}

interface AddFriendToUserPayload {
  addFriendUID: { accepted: string }
}

export function* ADD_FRIEND_TO_USER({
  payload,
}: Saga<AddFriendToUserPayload> & { payload: AddFriendToUserPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uFriendsListPagination } = getRoot().users
    const { uid } = getRoot().users.usersActive as ChatUser
    const { addFriendUID } = payload

    const success: UpdateFriendsResponse = yield call(
      userUpdateFriends,
      uid,
      addFriendUID,
      activeApp,
      getAppHost(appSettings),
    )
    if (
      success &&
      success.data &&
      success.data.accepted !== undefined &&
      success.data.accepted[addFriendUID.accepted] !== undefined &&
      success.data.accepted[addFriendUID.accepted].success
    ) {
      const friendsListPageNo =
        uFriendsListPagination !== undefined ? uFriendsListPagination.current_page : 1
      const friendsRes: UsersListResponse = yield call(
        userFriendList,
        uid,
        activeApp,
        getAppHost(appSettings),
        friendsListPageNo,
      )
      let totalUserFriendCount = friendsRes.data.length
      if (friendsRes.meta && friendsRes.meta.pagination && friendsRes.meta.pagination.total_pages) {
        totalUserFriendCount =
          friendsRes.meta.pagination.total_pages * friendsRes.meta.pagination.per_page
      }

      yield put({
        type: actions.SEARCH_USER_FRIEND,
      })
      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: friendsRes.data,
          uFriendsListTotal: totalUserFriendCount,
          uFriendsListPagination: friendsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      window.triggerNotification('Friend added to this user', 'success')
    } else {
      const friendsRes: UsersListResponse = yield call(
        userFriendList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )
      let totalUserFriendCount = friendsRes.data.length
      if (friendsRes.meta && friendsRes.meta.pagination && friendsRes.meta.pagination.total_pages) {
        totalUserFriendCount = friendsRes.meta.pagination.total_pages * 10
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          friendUserSearchText: '',
        },
      })
      yield put({
        type: actions.SEARCH_USER_FRIEND,
      })
      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: friendsRes.data,
          uFriendsListTotal: totalUserFriendCount,
          uFriendsListPagination: friendsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      if (
        success &&
        success.data &&
        success.data.accepted !== undefined &&
        success.data.accepted[addFriendUID.accepted] !== undefined &&
        !success.data.accepted[addFriendUID.accepted].success &&
        success.data.accepted[addFriendUID.accepted].message !== undefined
      ) {
        window.triggerNotification(success.data.accepted[addFriendUID.accepted].message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to add friend', 'error')
  }
}

export function* ENTER_USER_FRIEND_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          friendUserSearchText: '',
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          friendUserSearchText: searchText,
        },
      })
      // Debounce + takeLatest (see ENTER_USER_TEXT): the >= 2 gate is gone, so
      // without it every keystroke fires an uncancelled request.
      yield delay(300)
      yield put({
        type: actions.SEARCH_USER_FRIEND,
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search users to add as friends', 'error')
  }
}

export function* SEARCH_USER_FRIEND() {
  try {
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const searchText = getRoot().users.friendUserSearchText
    const { uid } = getRoot().users.usersActive as ChatUser
    const success: UsersListResponse = yield call(
      usersSearch,
      activeApp,
      getAppHost(appSettings),
      searchText,
    )

    if (success && success.data) {
      let userNonFriendsList = success.data
      if (userNonFriendsList.length) {
        userNonFriendsList = _.filter(userNonFriendsList, function fn(unf: ChatUser) {
          return unf.uid !== uid
        })
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          userNonFriendsList,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userNonFriendsList: [],
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search users to add as friends', 'error')
  }
}

interface UnfriendUserPayload {
  friendUID: string
}

export function* UNFRIEND_USER({
  payload,
}: Saga<UnfriendUserPayload> & { payload: UnfriendUserPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uFriendsListPagination } = getRoot().users
    const { uid } = getRoot().users.usersActive as ChatUser
    const { friendUID } = payload
    const success: UpdateFriendsResponse = yield call(
      userRemoveFriends,
      uid,
      friendUID,
      activeApp,
      getAppHost(appSettings),
    )
    if (success && success.data) {
      const friendsListPageNo =
        uFriendsListPagination !== undefined && (uFriendsListPagination.count ?? 0) > 1
          ? uFriendsListPagination.current_page
          : 1

      const friendsRes: UsersListResponse = yield call(
        userFriendList,
        uid,
        activeApp,
        getAppHost(appSettings),
        friendsListPageNo,
      )
      let totalUserFriendCount = 0
      if (friendsRes.data.length) {
        if (friendsRes.meta && friendsRes.meta.pagination && friendsRes.meta.pagination.total_pages) {
          totalUserFriendCount = friendsRes.meta.pagination.total_pages * 10
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: friendsRes.data,
          uFriendsListTotal: totalUserFriendCount,
          uFriendsListPagination: friendsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      window.triggerNotification('User removed as a friend', 'success')
    } else {
      const friendsRes: UsersListResponse = yield call(
        userFriendList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )
      let totalUserFriendCount = 0
      if (friendsRes.data.length) {
        if (friendsRes.meta && friendsRes.meta.pagination && friendsRes.meta.pagination.total_pages) {
          totalUserFriendCount =
            friendsRes.meta.pagination.total_pages * friendsRes.meta.pagination.per_page
        }
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          uFriendsList: friendsRes.data,
          uFriendsListTotal: totalUserFriendCount,
          uFriendsListPagination: friendsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to unfriend user', 'error')
  }
}

interface NonFriendListPayload {
  data?: ChatApp
  appSettings?: any
  currentPage?: number
}

export function* NON_USER_FRIEND_LIST_MODAL({
  payload,
}: Saga<NonFriendListPayload> & { payload: NonFriendListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const searchText = getRoot().users.nonFriendSearchText
    const nonFriendPagination = getRoot().users.nonFriendListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && nonFriendPagination && nonFriendPagination.current_page !== undefined)
      pageNo = nonFriendPagination.current_page + 1

    const success: UsersListResponse = yield call(
      usersList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText || '',
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let nonFriendListPagination = [] as LoosePagination

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        nonFriendListPagination = success.meta.pagination
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonFriendList: success.data,
          nonFriendListTotal: totalUserCount,
          nonFriendListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonFriendList: [],
          nonFriendListTotal: 0,
          nonFriendListPagination: [],
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch users to add as friends', 'error')
  }
}

export function* ENTER_NON_FRIEND_LIST_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonFriendSearchText: '',
          areUsersLoading: true,
        },
      })
      yield put({
        type: actions.SEARCH_NON_FRIEND_LIST_MODAL,
        payload: {},
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonFriendSearchText: searchText,
          areUsersLoading: true,
        },
      })

      // Debounce + takeLatest (see ENTER_USER_TEXT). This handler never had a
      // length gate, so every keystroke fired an uncancelled request and a
      // slower earlier response could overwrite a newer one.
      yield delay(300)
      yield put({
        type: actions.SEARCH_NON_FRIEND_LIST_MODAL,
        payload: {},
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search users to add as friends', 'error')
  }
}

export function* SEARCH_NON_FRIEND_LIST_MODAL() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const searchText = getRoot().users.nonFriendSearchText
    const success: UsersListResponse = yield call(
      usersSearch,
      activeApp,
      getAppHost(appSettings),
      searchText || '',
    )

    if (success && success.data) {
      let totalUserCount = success.data.length
      let nonFriendListPagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        nonFriendListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          nonFriendList: success.data,
          nonFriendListTotal: totalUserCount,
          nonFriendListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonFriendListTotal: 0,
          nonFriendListPagination: [],
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search users to add as friends', 'error')
  }
}

interface UserGroupsListPayload {
  currentPage?: number
}

export function* USER_GROUPS_LIST({
  payload,
}: Saga<UserGroupsListPayload> & { payload: UserGroupsListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uid } = getRoot().users.usersActive as ChatUser
    const { uGroupsListPagination } = getRoot().users

    let pageNo = payload.currentPage
    if (pageNo === undefined && uGroupsListPagination && uGroupsListPagination.current_page !== undefined)
      pageNo = uGroupsListPagination.current_page + 1

    const success: UserGroupsListResponse = yield call(
      userGroupList,
      uid,
      activeApp,
      getAppHost(appSettings),
      pageNo,
    )

    if (success && success.data) {
      let totalUserGroupCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserGroupCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: success.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: success.meta.pagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: [],
          uGroupsListTotal: 0,
          uGroupsListPagination: [],
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch groups', 'error')
  }
}

export function* ENTER_USER_GROUP_LIST_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userGroupSearchText: '',
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userGroupSearchText: searchText,
          areUsersLoading: true,
        },
      })
      // Debounce + takeLatest (see ENTER_USER_TEXT): the >= 2 gate is gone, so
      // without it every keystroke fires an uncancelled request.
      yield delay(300)
      yield put({
        type: actions.SEARCH_GROUP_FRIEND_LIST,
        payload: {},
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search groups', 'error')
  }
}

export function* SEARCH_GROUP_FRIEND_LIST() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const { appSettings } = getRoot().apps
    const searchText = getRoot().users.userGroupSearchText
    const success: UserGroupsListResponse = yield call(
      userGroupSearch,
      (getRoot().users.usersActive as ChatUser).uid,
      getRoot().apps.active,
      getAppHost(appSettings),
      searchText,
    )

    if (success && success.data) {
      let totalUserGroupCount = success.data.length

      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalUserGroupCount = success.meta.pagination.total_pages * 10
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: success.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: success.meta.pagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: [],
          uGroupsListTotal: 0,
          uGroupsListPagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search groups', 'error')
  }
}

interface KickBanGroupPayload {
  kickGUID?: string
  guid?: string
}

export function* KICK_USER_FROM_GROUP({
  payload,
}: Saga<KickBanGroupPayload> & { payload: KickBanGroupPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uGroupsListPagination } = getRoot().users
    const { uid } = getRoot().users.usersActive as ChatUser
    const { kickGUID } = payload

    const success: GroupMembersChangeResponse = yield call(
      kickUserFromGroup,
      uid,
      kickGUID,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      const groupsListPageNo =
        uGroupsListPagination !== undefined && (uGroupsListPagination.count ?? 0) > 1
          ? uGroupsListPagination.current_page
          : 1

      const groupsRes: UserGroupsListResponse = yield call(
        userGroupList,
        uid,
        activeApp,
        getAppHost(appSettings),
        groupsListPageNo,
      )
      let totalUserGroupCount = 0
      if (groupsRes.data.length) {
        if (groupsRes.meta && groupsRes.meta.pagination && groupsRes.meta.pagination.total_pages) {
          totalUserGroupCount = groupsRes.meta.pagination.total_pages * 10
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: groupsRes.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: groupsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      window.triggerNotification('User removed from group', 'success')
    } else {
      const groupsRes: UserGroupsListResponse = yield call(
        userGroupList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )
      let totalUserGroupCount = 0
      if (groupsRes.data.length) {
        if (groupsRes.meta && groupsRes.meta.pagination && groupsRes.meta.pagination.total_pages) {
          totalUserGroupCount =
            groupsRes.meta.pagination.total_pages * groupsRes.meta.pagination.per_page
        }
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: groupsRes.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: groupsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      if (success.error.message !== undefined) {
        if (success.error.code === 'ERR_GROUP_NO_CLEARANCE') {
          window.triggerNotification('Owner cannot be kicked', 'error')
        } else {
          window.triggerNotification(success.error.message, 'error')
        }
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to remove user from group', 'error')
  }
}

export function* BAN_USER_FROM_GROUP({
  payload,
}: Saga<KickBanGroupPayload> & { payload: KickBanGroupPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uid, name } = getRoot().users.usersActive as ChatUser
    const { guid } = payload

    const success: GroupMembersChangeResponse = yield call(
      banUserFromGroup,
      uid,
      guid,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data) {
      const groupsRes: UserGroupsListResponse = yield call(
        userGroupList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )
      let totalUserGroupCount = 0
      if (groupsRes.data.length) {
        if (groupsRes.meta && groupsRes.meta.pagination && groupsRes.meta.pagination.total_pages) {
          totalUserGroupCount = groupsRes.meta.pagination.total_pages * 10
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: groupsRes.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: groupsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      window.triggerNotification(`${name} banned from group`, 'success')
    } else {
      const groupsRes: UserGroupsListResponse = yield call(
        userGroupList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )
      let totalUserGroupCount = 0
      if (groupsRes.data.length) {
        if (groupsRes.meta && groupsRes.meta.pagination && groupsRes.meta.pagination.total_pages) {
          totalUserGroupCount = groupsRes.meta.pagination.total_pages * 10
        }
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: groupsRes.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: groupsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      if (success.error.message !== undefined) {
        if (success.error.code === 'ERR_GROUP_NO_CLEARANCE') {
          window.triggerNotification('Owner cannot be banned', 'error')
        } else {
          window.triggerNotification('Error while banning member', 'error')
        }
      } else {
        window.triggerNotification('Unknown Error', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to ban user from group', 'error')
  }
}

interface AddUserToGroupPayload {
  addGUID: string
}

export function* ADD_USER_TO_GROUP({
  payload,
}: Saga<AddUserToGroupPayload> & { payload: AddUserToGroupPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const { uGroupsListPagination } = getRoot().users
    const { uid, name } = getRoot().users.usersActive as ChatUser
    const { addGUID } = payload

    const success: GroupMembersChangeResponse = yield call(
      addUserToGroup,
      uid,
      addGUID,
      activeApp,
      getAppHost(appSettings),
    )
    if (
      success &&
      success.data &&
      success.data.participants !== undefined &&
      success.data.participants[uid] !== undefined &&
      success.data.participants[uid].success
    ) {
      const groupsListPageNo =
        uGroupsListPagination !== undefined ? uGroupsListPagination.current_page : 1
      const groupsRes: UserGroupsListResponse = yield call(
        userGroupList,
        uid,
        activeApp,
        getAppHost(appSettings),
        groupsListPageNo,
      )
      let totalUserGroupCount = groupsRes.data.length
      if (groupsRes.meta && groupsRes.meta.pagination && groupsRes.meta.pagination.total_pages) {
        totalUserGroupCount = groupsRes.meta.pagination.total_pages * 10
      }

      yield put({
        type: actions.SEARCH_USER_GROUP,
      })
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: groupsRes.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: groupsRes.meta.pagination,
          areUsersLoading: false,
        },
      })
      window.triggerNotification(`${name} added to group`, 'success')
    } else if (
      success &&
      success.data &&
      success.data.participants !== undefined &&
      success.data.participants[uid] !== undefined &&
      !success.data.participants[uid].success
    ) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          areUsersLoading: false,
        },
      })
      window.triggerNotification('Already a Member', 'error')
    } else {
      const groupsRes: UserGroupsListResponse = yield call(
        userGroupList,
        uid,
        activeApp,
        getAppHost(appSettings),
      )
      let totalUserGroupCount = groupsRes.data.length
      if (groupsRes.meta && groupsRes.meta.pagination && groupsRes.meta.pagination.total_pages) {
        totalUserGroupCount = groupsRes.meta.pagination.total_pages * 10
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupUserSearchText: '',
        },
      })
      yield put({
        type: actions.SEARCH_USER_GROUP,
      })
      yield put({
        type: actions.SET_STATE,
        payload: {
          uGroupsList: groupsRes.data,
          uGroupsListTotal: totalUserGroupCount,
          uGroupsListPagination: groupsRes.meta.pagination,
          areUsersLoading: false,
        },
      })

      if (success.error.message !== undefined) {
        if (success.error.code === 'ERR_GROUP_NO_CLEARANCE') {
          window.triggerNotification('GROUP ACTION NOT ALLOWED', 'error')
        } else {
          window.triggerNotification(success.error.message, 'error')
        }
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to add user to group', 'error')
  }
}

export function* NON_USER_GROUP_LIST_MODAL({
  payload,
}: Saga<NonFriendListPayload> & { payload: NonFriendListPayload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })
    if (!payload.data) payload.data = getRoot().apps.active
    if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings
    const searchText = getRoot().users.nonGroupSearchText
    const nonGroupPagination = getRoot().users.nonGroupListPagination

    let pageNo = payload.currentPage
    if (pageNo === undefined && nonGroupPagination && nonGroupPagination.current_page !== undefined)
      pageNo = nonGroupPagination.current_page + 1

    const success: any = yield call(
      groupsList,
      payload.data,
      getAppHost(payload.appSettings),
      pageNo,
      searchText || '',
    )

    if (success && success.data) {
      let totalGroupCount = success.data.length
      let nonGroupListPagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        nonGroupListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          nonGroupList: success.data,
          nonGroupListTotal: totalGroupCount,
          nonGroupListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonGroupList: [],
          nonGroupListTotal: 0,
          nonGroupListPagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to fetch groups to add user to', 'error')
  }
}

export function* ENTER_NON_GROUP_LIST_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonGroupSearchText: '',
          areUsersLoading: true,
        },
      })
      yield put({
        type: actions.SEARCH_NON_GROUP_LIST_MODAL,
        payload: {},
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonGroupSearchText: searchText,
          areUsersLoading: true,
        },
      })

      // Debounce + takeLatest (see ENTER_USER_TEXT). This handler never had a
      // length gate, so every keystroke fired an uncancelled request and a
      // slower earlier response could overwrite a newer one.
      yield delay(300)
      yield put({
        type: actions.SEARCH_NON_GROUP_LIST_MODAL,
        payload: {},
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search groups to add user to', 'error')
  }
}

export function* SEARCH_NON_GROUP_LIST_MODAL() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: true,
      },
    })

    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const searchText = getRoot().users.nonGroupSearchText
    const success: any = yield call(groupsSearch, activeApp, getAppHost(appSettings), searchText || '')
    if (success && success.data) {
      let totalGroupCount = success.data.length
      let nonGroupListPagination = [] as LoosePagination
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalGroupCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        nonGroupListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          nonGroupList: success.data,
          nonGroupListTotal: totalGroupCount,
          nonGroupListPagination,
          areUsersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          nonGroupList: [],
          nonGroupListTotal: 0,
          nonGroupListPagination: {},
          areUsersLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search groups to add user to', 'error')
  }
}

export function* ENTER_USER_GROUP_TEXT({
  payload,
}: Saga<EnterTextPayload> & { payload: EnterTextPayload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupUserSearchText: '',
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          groupUserSearchText: searchText,
        },
      })
      // Debounce + takeLatest (see ENTER_USER_TEXT): the >= 2 gate is gone, so
      // without it every keystroke fires an uncancelled request.
      yield delay(300)
      yield put({
        type: actions.SEARCH_USER_GROUP,
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search groups', 'error')
  }
}

export function* SEARCH_USER_GROUP() {
  try {
    const activeApp = getRoot().apps.active
    const { appSettings } = getRoot().apps
    const searchText = getRoot().users.groupUserSearchText

    const success: any = yield call(groupsSearch, activeApp, getAppHost(appSettings), searchText)

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userNonGroupsList: success.data,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          userNonGroupsList: [],
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        areUsersLoading: false,
      },
    })
    window.triggerNotification('Failed to search groups', 'error')
  }
}

export default function* usersSaga() {
  yield all([
    takeEvery(actions.USERS_LIST, USERS_LIST as SagaWorker),
    takeLatest(actions.USERS_COUNT, USERS_COUNT as SagaWorker),
    takeEvery(actions.USERS_INACTIVE_LIST, INACTIVE_USERS_LIST as SagaWorker),
    takeEvery(actions.ADD_USER, withCallback(ADD_USER) as SagaWorker),
    takeLatest(actions.ENTER_USER_TEXT, ENTER_USER_TEXT as SagaWorker),
    takeLatest(actions.ENTER_USER_INACTIVE_TEXT, ENTER_INACTIVE_USER_TEXT as SagaWorker),
    takeLatest(actions.SEARCH_INACTIVE_USER, SEARCH_INACTIVE_USER as SagaWorker),
    takeLatest(actions.SEARCH_USER, SEARCH_USER as SagaWorker),
    takeEvery(actions.GET_USER, GET_USER as SagaWorker),
    takeEvery(actions.UPDATE_USER, UPDATE_USER as SagaWorker),
    takeEvery(actions.DELETE_USER, DELETE_USER as SagaWorker),
    takeEvery(actions.DEACTIVATE_USER, DEACTIVATE_USER as SagaWorker),
    takeEvery(actions.ACTIVATE_USER, ACTIVATE_USER as SagaWorker),
    takeEvery(actions.USER_AUTH_TOKENS_LIST, USER_AUTH_TOKENS_LIST as SagaWorker),
    takeEvery(actions.DELETE_USER_AUTH_TOKEN, DELETE_USER_AUTH_TOKEN as SagaWorker),
    takeEvery(actions.ADD_USER_AUTH_TOKEN, ADD_USER_AUTH_TOKEN as SagaWorker),
    takeLatest(actions.ENTER_UAUTHTOKEN_TEXT, ENTER_UAUTHTOKEN_TEXT as SagaWorker),
    takeLatest(actions.SEARCH_UAUTHTOKEN, SEARCH_UAUTHTOKEN as SagaWorker),
    takeEvery(actions.UPDATE_USER_FRIENDS, UPDATE_USER_FRIENDS as SagaWorker),
    takeEvery(actions.KICK_USER_FROM_GROUP, withCallback(KICK_USER_FROM_GROUP) as SagaWorker),
    takeEvery(actions.BAN_USER_FROM_GROUP, withCallback(BAN_USER_FROM_GROUP) as SagaWorker),
    takeEvery(actions.ADD_USER_TO_GROUP, withCallback(ADD_USER_TO_GROUP) as SagaWorker),
    takeEvery(actions.USER_FRIENDS_LIST, USER_FRIENDS_LIST as SagaWorker),
    takeLatest(actions.SEARCH_USER_FRIEND, SEARCH_USER_FRIEND as SagaWorker),
    takeLatest(actions.ENTER_USER_FRIEND_TEXT, ENTER_USER_FRIEND_TEXT as SagaWorker),
    takeEvery(actions.ADD_FRIEND_TO_USER, withCallback(ADD_FRIEND_TO_USER) as SagaWorker),
    takeEvery(actions.UNFRIEND_USER, withCallback(UNFRIEND_USER) as SagaWorker),
    takeLatest(actions.ENTER_USER_FRIEND_LIST_TEXT, ENTER_USER_FRIEND_LIST_TEXT as SagaWorker),
    takeLatest(actions.SEARCH_USER_FRIEND_LIST, SEARCH_USER_FRIEND_LIST as SagaWorker),
    takeLatest(actions.ENTER_USER_GROUP_LIST_TEXT, ENTER_USER_GROUP_LIST_TEXT as SagaWorker),
    takeLatest(actions.SEARCH_GROUP_FRIEND_LIST, SEARCH_GROUP_FRIEND_LIST as SagaWorker),
    takeEvery(actions.NON_USER_FRIEND_LIST_MODAL, NON_USER_FRIEND_LIST_MODAL as SagaWorker),
    takeLatest(actions.ENTER_NON_FRIEND_LIST_TEXT, ENTER_NON_FRIEND_LIST_TEXT as SagaWorker),
    takeLatest(actions.SEARCH_NON_FRIEND_LIST_MODAL, SEARCH_NON_FRIEND_LIST_MODAL as SagaWorker),
    takeEvery(actions.NON_USER_GROUP_LIST_MODAL, NON_USER_GROUP_LIST_MODAL as SagaWorker),
    takeLatest(actions.ENTER_NON_GROUP_LIST_TEXT, ENTER_NON_GROUP_LIST_TEXT as SagaWorker),
    takeLatest(actions.SEARCH_NON_GROUP_LIST_MODAL, SEARCH_NON_GROUP_LIST_MODAL as SagaWorker),
    takeEvery(actions.SHOW_IMPORT_USER_REQUEST, SHOW_IMPORT_USER_REQUEST as SagaWorker),
    takeEvery(
      actions.IMPORT_USERS_REQUEST_MANUAL_INTENT,
      withCallback(IMPORT_USERS_REQUEST_MANUAL_INTENT) as SagaWorker,
    ),
    takeEvery(actions.AI_AGENT_USER_LIST, AI_AGENT_USER_LIST as SagaWorker),
    takeEvery(actions.USER_GROUPS_LIST, USER_GROUPS_LIST as SagaWorker),
    takeLatest(actions.SEARCH_USER_GROUP, SEARCH_USER_GROUP as SagaWorker),
    takeLatest(actions.ENTER_USER_GROUP_TEXT, ENTER_USER_GROUP_TEXT as SagaWorker),
  ])
}
